import Receipt from "@/assets/icons/Receipt";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import { Box } from "@/components/ui/box";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { usePassRedeem } from "@/hooks/usePassRedeem";
import { useReceipt } from "@/hooks/useReceipt";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { T, useT } from "@transifex/react";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useState } from "react";
import { Pressable } from "react-native";

export default function RedeemConfirmation() {
  const [focused, setFocused] = useState<boolean>(false);
  const { passID, termsAndConditionsOKTimestamp } = useLocalSearchParams<{ passID: string; termsAndConditionsOKTimestamp: string }>();
  const { data: passRedeemData, isPending, phoneNumber } = usePassRedeem(focused, passID, termsAndConditionsOKTimestamp);
  const receiptID = passRedeemData?.receiptID ?? "";
  const { isPending: isReceiptPending, formattedEndTime } = useReceipt(receiptID);
  const t = useT();

  useFocusEffect(
    useCallback(() => {
      setFocused(true);
      return () => setFocused(false);
    }, []),
  );

  if (isPending || isReceiptPending) {
    return <RedeemConfirmationScreenSkeleton />;
  }

  return (
    <ProtectedRoute>
      <Screen>
        <VStack className="gap-[32px]">
          <PageTitle title="Redemption Complete!" />
          <Box>
            <Text className="text-center text-typography-900 text-[16px] leading-[22px] align-middle">
              <T _str="Your FlexPark session has been activated. " />
            </Text>
            <Text className="text-center text-typography-900 text-[16px] leading-[22px] align-middle">
              <T _str="You’re all set!" />
            </Text>
          </Box>
          <Card className="gap-[16px] pt-[44px] pb-[24px] px-[20px]">
            <Text className="font-[600] text-[16px] leading-[22px]  text-center align-middle text-typography-900">
              <T _str="This parking session ends at" />
            </Text>
            <Text className="font-[700] text-[30px] leading-[100%] text-center align-middle text-typography-900">{formattedEndTime}</Text>
            <Box className="gap-[4px]">
              {phoneNumber && (
                <>
                  <Text className="font-[400] text-[14px] leading-[20px] tracking-0 text-center align-middle text-typography-900">
                    <T _str="Your receipt has been sent to:" />
                  </Text>
                  <Text className="font-[700] text-[14px] leading-[20px] tracking-0 text-center align-middle text-typography-900">
                    {formatPhoneNumber(phoneNumber ?? "")}
                  </Text>
                </>
              )}
            </Box>
            <Box className="pt-[24px]">
              <AppButton
                onPress={() => router.push({ pathname: "/receipt", params: { receiptID } })}
                title={t("View Receipt")}
                variant="primary"
                icon={Receipt}
                iconPosition="before"
              />
            </Box>
          </Card>

          <Pressable onPress={() => router.replace("/welcome")}>
            <Text className="text-typography-900 text-[14px] leading-[20px] text-center font-[700] underline decoration-solid">
              <T _str="Return to purchase menu" />
            </Text>
          </Pressable>
        </VStack>
      </Screen>
    </ProtectedRoute>
  );
}

function RedeemConfirmationScreenSkeleton() {
  return (
    <Screen>
      <VStack className="gap-[32px]">
        <SkeletonText className="h-[20px] w-[70%] mx-auto" />
        <Box>
          <SkeletonText _lines={2} className="h-[16px] w-[80%] mx-auto" />
        </Box>
        <Card className="gap-[16px] pt-[44px] pb-[24px] px-[20px]">
          <SkeletonText className="h-[16px] w-[180px] mx-auto" />
          <Skeleton className="h-[30px] w-[120px] mx-auto mt-2" />
          <SkeletonText _lines={2} className="h-[14px] w-[200px] mt-2 mx-auto" />
          <Box className="pt-[24px]">
            <Skeleton className="h-[52px] w-full mx-auto rounded-lg" />
          </Box>
        </Card>
        <SkeletonText className="h-[14px] w-[200px] mx-auto underline" />
      </VStack>
    </Screen>
  );
}
